import { inject, injectable } from "inversify";
import { TicketEntity } from "@dropDesk/domain/entities/ticket/ticket.entity";
import api, { parseServerError } from "@dropDesk/data/clients/http.client";
import { ListPaginationEntity } from "@dropDesk/domain/entities/common/list_pagination.entity";
import { from, mergeMap, Observable, Observer } from "rxjs";
import { apolloClient } from "@dropDesk/data/clients/apollo.client";
import { gql } from "@apollo/client";
import { UserEntity } from "@dropDesk/domain/entities/user/user.entity";
import { CancelTokenSource } from "axios";
import { TicketMessageEntity } from "@dropDesk/domain/entities/ticket/message/ticket_message.entity";
import {
    ChatIntegrationConnectionEntity,
    ConnectionExtraInfoEntity
} from "@dropDesk/domain/entities/chat/chat_integration_connection.entity";
import { getIdCompanyByLocalStorage } from "@dropDesk/utils/helpers/common";
import { StatusTypes, SubscriptionType } from "@dropDesk/domain/entities/chat/chat_enum";
import { ShortTicketEntity } from "@dropDesk/domain/entities/ticket/short_ticket";
import { RemoteStorageDatasource } from "@dropDesk/data/data_source/remote_storage/remote_storage_datasource";
import { ListPaginationChatEntity } from "@dropDesk/domain/entities/chat/list_pagination_chat.entity";
import { TypeListChat } from "@dropDesk/presentation/pages/chat/controller/chat.controller";
import { newSystemMessage } from "@dropDesk/presentation/pages/chat/controller/new_message";
import { DropDeskErrorEnum } from "@dropDesk/domain/entities/exceptions/dropdesk_error_enum";
import { disconnect, socket, socketConnect } from "@dropDesk/data/clients/socket.client";
import { getToken } from "@dropDesk/data/data_source/token/token.datasource";
import { SocketResponse, SocketResponseEntity } from "@dropDesk/domain/entities/chat/chat_socket";
import { TicketNotificationEntity } from "@dropDesk/domain/entities/ticket/ticket_notifications_entity";
import { AllChatsEntity } from "@dropDesk/domain/entities/chat/all_chats.entity";
import { SocketChatIntegrationResponse } from "@dropDesk/domain/entities/chat/chat_integration_socket.entity";
import { ListPaginationAllChatsEntity } from "@dropDesk/domain/entities/chat/list_pagination_chat_all_entity";
import { VoidSuccess } from "@dropDesk/domain/entities/response/response";
import { ConstantsKeys } from "@dropDesk/domain/entities/constants/constants_keys";
import { ListPaginationChatMessagesEntity } from "@dropDesk/domain/entities/chat/list_pagination_chat_messages.entity";
import { ServerError } from "@dropDesk/domain/entities/exceptions/exceptions";

@injectable()
export abstract class ChatRemoteDataSource {
    public abstract listChatsAttending(searchParam: string, page: number): Promise<ListPaginationEntity<TicketEntity>>;

    public abstract listChatsWaiting(searchParam: string, page: number): Promise<ListPaginationEntity<TicketEntity>>;

    public abstract subscribe(id?: string, ticket?: TicketEntity | null): Observable<TicketEntity[]>;

    public abstract loadMessages(chatId: string, page: number, cancelTokenSource?: CancelTokenSource): Promise<ListPaginationEntity<TicketMessageEntity>>;

    public abstract loadMessagesV2(ticketId: string, page: number, limit: number, cancelTokenSource?: CancelTokenSource, direction?: 'up' | 'down'): Promise<ListPaginationChatMessagesEntity>;

    public abstract listChatMessagesAfterSubscription(chatId: string): Promise<TicketMessageEntity[]>;

    public abstract startConnection(): Promise<ChatIntegrationConnectionEntity>;

    public abstract logOut(force: boolean): Promise<VoidSuccess>;

    public abstract connectionState(): Promise<ChatIntegrationConnectionEntity>;

    public abstract subscribeConnection(
        id: string,
        useSocket: boolean,
        handleDisconnect: (temporary: boolean) => void,
        getLastUpdates: () => void
    ): Observable<ChatIntegrationConnectionEntity | null>;

    public abstract sendMessage(message: TicketMessageEntity, associateSenderToTicket: boolean): Promise<TicketMessageEntity>;

    public abstract delete(id: string): Promise<void>;

    public abstract subscriptionUploadFile(message: TicketMessageEntity): Observable<TicketMessageEntity>;

    public abstract editMessage(idMessage: string, text: string): Promise<void>;

    public abstract navigateToMessage(id: string, ticketNumber: number): Promise<ListPaginationEntity<TicketMessageEntity>>;

    public abstract navigateToMessageV2(id: string, limit: number): Promise<ListPaginationChatMessagesEntity>;

    public abstract reactMessage(
        messageId: string,
        reactionId: string,
        reaction: string,
    ): Promise<VoidSuccess>;

    public abstract informReadMessages(ticketId: string, cancelTokenSource?: CancelTokenSource): Promise<void>;

    public abstract updateUsersInfoCache(users: UserEntity[]): void;

    public abstract clearTicketsCache(): void;

    public abstract joinTicket(ticketId: string): void;

    public abstract leaveTicket(ticketId: string): void;

    public abstract listAllChats(searchParam: string, cancelTokenSource?: CancelTokenSource): Promise<AllChatsEntity>;

    public abstract updateCacheFromSingleTicket(ticket: TicketEntity): void;

    public abstract getTicketFromCache(id: string): TicketEntity | undefined;

    public abstract getUsersFromCache(ids: string[]): Promise<UserEntity[]>;

    public abstract getMessageById(id: string): Promise<TicketMessageEntity>;

    public abstract resendMessage(id: string): Promise<TicketMessageEntity>;
}

@injectable()
export class ChatRemoteDataSourceImpl implements ChatRemoteDataSource {

    private _remoteDataSource: RemoteStorageDatasource;


    constructor(
        @inject(RemoteStorageDatasource) dataSource: RemoteStorageDatasource,
    ) {
        this._remoteDataSource = dataSource;
    }

    table: string = 'chat';
    usersInfo: Record<string, UserEntity> = {};
    localCache: TicketEntity[] = [];
    processingReceived: string[] = [];
    processingRead: string[] = [];

    private get user(): UserEntity {
        return UserEntity.fromJson(JSON.parse(localStorage.getItem(process.env.STORAGE_USER as string)!));
    }

    private resolveListChatResponse(listPaginationChatEntity: ListPaginationChatEntity, type: TypeListChat): ListPaginationEntity<TicketEntity> {
        const tickets = listPaginationChatEntity.data;
        let data: TicketEntity[] = [];

        if (tickets.length > 0) {
            data = tickets.map((entry: TicketEntity) => TicketEntity.fromJson(entry).copyWith({
                countUnreadNotification: listPaginationChatEntity.unreadMessages?.[type]?.data?.find((ticket) => ticket?.idTicket === entry.id)?.total
            }));
            this.updateUsersInfoCache(data.map((ticket) => ticket.usersInfo!).reduce((prev, curr) => [...prev, ...curr]));
        }
        if (type === TypeListChat.attending) {
            this.addNotificationsDependant(data);
        }

        const result = new ListPaginationEntity<TicketEntity>({
            page: listPaginationChatEntity.page,
            limit: ConstantsKeys.defaultLimit,
            totalRows: listPaginationChatEntity.totalRows,
            hasMore: listPaginationChatEntity.page < (listPaginationChatEntity.pages - 1),
            pages: listPaginationChatEntity.pages,
            data,
        });

        this.updateLocalCache(result.data, null, false, false);
        return result;
    }


    public async startConnection(): Promise<ChatIntegrationConnectionEntity> {
        return new Promise<ChatIntegrationConnectionEntity>(async (resolve, reject) => {
            try {
                const response = await api.get<ChatIntegrationConnectionEntity>(`${this.table}/startConnection`);
                return resolve(new ChatIntegrationConnectionEntity({
                    ...response.data
                }));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public logOut(force: boolean): Promise<VoidSuccess> {
        return new Promise<VoidSuccess>(async (resolve, reject) => {
            try {
                const response = await api.get<VoidSuccess>(`${this.table}/logout?force=${force}`);
                return resolve(response.data);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public connectionState(): Promise<ChatIntegrationConnectionEntity> {
        return new Promise<ChatIntegrationConnectionEntity>(async (resolve, reject) => {
            try {
                const response = await api.get<ChatIntegrationConnectionEntity>(`${this.table}/connectionState`);
                const result = response.data;
                const chatConnection = ChatIntegrationConnectionEntity.fromJson(result);
                return resolve(chatConnection);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    private subscribeConnectionGql(): Observable<ChatIntegrationConnectionEntity> {
        const idCompany = getIdCompanyByLocalStorage();
        const table = 'chat_integration_connection';
        const queryString =
            `subscription ChatConnection {
              ${table}(limit: 1, where: {idCompany: {_eq: "${idCompany}"}}) {
                status
                idCompany
                qrCode
                extraInfo
              }
            }`;
        const query = gql`${queryString}`;

        return new Observable<ChatIntegrationConnectionEntity>((observer: Observer<ChatIntegrationConnectionEntity>) => {
            return apolloClient.subscribe({
                query
            }).subscribe({
                next: (response) => {
                    const connection = response.data[table][0] as ChatIntegrationConnectionEntity | undefined;
                    const chatConnection = new ChatIntegrationConnectionEntity({
                        idCompany: idCompany,
                        qrCode: connection && connection.qrCode ? connection.qrCode : null,
                        status: connection && connection.status ? connection.status : StatusTypes.DISCONNECTED,
                        updatedAt: connection && connection.updatedAt ? connection.updatedAt : undefined,
                        createdAt: connection && connection.createdAt ? connection.createdAt : undefined,
                        extraInfo: connection && connection.extraInfo ? ConnectionExtraInfoEntity.fromJson(connection.extraInfo) : undefined,
                    });
                    observer.next(chatConnection);

                },
                error(err: any) {
                    observer.error(err);
                },
            });
        });
    }

    public subscribeConnection(
        id: string,
        useSocket: boolean,
        handleDisconnect: (temporary: boolean) => void,
        getLastUpdates: () => void
    ): Observable<ChatIntegrationConnectionEntity | null> {
        if (useSocket) {
            return this.subscribeConnectionSocket(id, handleDisconnect, getLastUpdates);
        }
        return this.subscribeConnectionGql();
    }

    private subscribeConnectionSocket(
        id: string,
        handleDisconnect: (temporary: boolean) => void,
        getLastUpdates: () => void
    ): Observable<ChatIntegrationConnectionEntity | null> {
        return new Observable<ChatIntegrationConnectionEntity | null>((observer: Observer<ChatIntegrationConnectionEntity | null>) => {
            return from(getToken()).pipe(mergeMap((token: string | null) => {
                return new Observable(() => {
                    socketConnect(
                        { authorization: `Bearer ${token}`, id },
                        handleDisconnect,
                        getLastUpdates,
                        null,
                        id,
                        'chatIntegration'
                    );

                    socket!.on('message', async (response: any) => {
                        try {
                            const result = SocketChatIntegrationResponse.fromJson(response);
                            if (result.hasUnknownError) {
                                disconnect(['message', id]);
                                handleDisconnect(false);
                                observer.next(null);
                            } else {
                                observer.next(result.chatIntegration ?? null);
                            }
                        } catch (error) {
                            console.error(error);
                        }
                    });
                    return () => disconnect(['message', id]);
                });
            })
            ).subscribe();
        });
    }

    public listAllChats(searchParam: string, cancelTokenSource?: CancelTokenSource): Promise<AllChatsEntity> {
        return new Promise<AllChatsEntity>(async (resolve, reject) => {
            try {
                this.clearTicketsCache();
                const response = await api.get(`${this.table}/listAll?searchParam=${searchParam ? encodeURIComponent(searchParam) : ''}`, { cancelToken: cancelTokenSource?.token });
                const result: ListPaginationAllChatsEntity = new ListPaginationAllChatsEntity({ ...response.data });
                return resolve(new AllChatsEntity({
                    attending: this.resolveListChatResponse(result.userChats, TypeListChat.attending),
                    waiting: this.resolveListChatResponse(result.waitingChats, TypeListChat.waiting),
                }));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public listChatsAttending(searchParam: string, page: number): Promise<ListPaginationEntity<TicketEntity>> {
        return new Promise<ListPaginationEntity<TicketEntity>>(async (resolve, reject) => {
            try {
                const response =
                    await api.get(`${this.table}/list?page=${page}&type=myChats&searchParam=${searchParam ? encodeURIComponent(searchParam) : ''}`);
                return resolve(this.resolveListChatResponse(response.data, TypeListChat.attending));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public listChatsWaiting(searchParam: string, page: number): Promise<ListPaginationEntity<TicketEntity>> {
        return new Promise<ListPaginationEntity<TicketEntity>>(async (resolve, reject) => {
            try {
                const response = await api.get(`${this.table}/list?page=${page}&type=waiting&searchParam=${searchParam ? encodeURIComponent(searchParam) : ''}`);
                return resolve(this.resolveListChatResponse(response.data, TypeListChat.waiting));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public clearTicketsCache(): void {
        this.updateLocalCache([], null, false, true);
    }

    public joinTicket(ticketId: string): void {
        socket?.emit('joinTicket', { ticketId });
    }

    public leaveTicket(ticketId: string): void {
        socket?.emit('leaveTicket', { ticketId });
    }

    private async getUsersFromServer(ids: string[]): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                const response = await api.get(`users/byIds?ids=${ids.join(',')}`);
                const users = response.data as UserEntity[];
                this.updateUsersInfoCache(users);
                return resolve();
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public updateUsersInfoCache(data: UserEntity[]): void {
        for (const user of data) {
            this.usersInfo[user.id] = user;
        }
    }

    public async getUsersFromCache(ids: string[]): Promise<UserEntity[]> {
        const users: UserEntity[] = [];
        const missingUserIds: string[] = [];

        for (const id of ids) {
            const cachedUser = this.usersInfo[id];
            if (cachedUser) {
                users.push(cachedUser);
            } else {
                missingUserIds.push(id);
            }
        }

        if (missingUserIds.length > 0) {
            await this.getUsersFromServer(missingUserIds);
            for (const userId of missingUserIds) {
                const user = this.usersInfo[userId];
                if (user) {
                    users.push(user);
                }
            }
        }

        return users;
    }

    private makeDecodedTickets(): TicketEntity[] {
        return this.localCache.map((entry: TicketEntity) => TicketEntity.fromJson(new TicketEntity({
            ...entry,
            usersInfo: entry.usersInfo,
            lastMessage: !!entry.lastMessage && !entry.lastMessage?.sender ?
                entry.lastMessage.copyWith({
                    ...entry.lastMessage,
                    sender: entry.usersInfo?.find((user) => user.id === entry.lastMessage!.senderId) ??
                        UserEntity.fromJson({
                            id: entry.lastMessage!.senderId,
                            name: entry.lastMessage?.pushName ?? '',
                        })
                }) : entry.lastMessage
        })));
    }

    private canKeepInChatCache(item: TicketEntity, fromSingleTicket: string | null | undefined): boolean {
        if (fromSingleTicket) {
            return item.id === fromSingleTicket;
        }
        return item.hasEligibleChat(this.user);
    }

    private filterTicketsForChatCache(tickets: TicketEntity[], fromSingleTicket: string | null | undefined): TicketEntity[] {
        return tickets.filter((item) => this.canKeepInChatCache(item, fromSingleTicket));
    }

    private async getTicketsAfterSubs(tickets: ShortTicketEntity[] | TicketEntity[], fromSingleTicket: string | null): Promise<TicketEntity[]> {
        this.processReceivedMessagesSocket(tickets as TicketEntity[], fromSingleTicket, true);
        return this.makeDecodedTickets();
    }

    private getLocalCacheDeepCopy(): TicketEntity[] {
        const copy = JSON.parse(JSON.stringify(this.localCache)) as any[];
        return copy.map((entry) => TicketEntity.fromJson(entry));
    }

    private updateLocalCache(list: TicketEntity[], fromSingleTicket: string | null | undefined, fromSubscription: boolean, clearIfEmpty = true) {
        if (list.length === 0 && clearIfEmpty) {
            this.localCache = [];
        }
        if (list.length) {
            const deepLocalCacheCopy = this.getLocalCacheDeepCopy();
            const startingChats = fromSingleTicket
                ? []
                : this.localCache.filter((ticket) => ticket.isMeStartingChat);
            list = [...list, ...startingChats];
            for (const item of list) {
                if (fromSingleTicket && item.id !== fromSingleTicket) {
                    continue;
                }
                const index = deepLocalCacheCopy.findIndex((ticketCache) => ticketCache.id === item.id);
                const checkPermission = this.canKeepInChatCache(item, fromSingleTicket);
                if (index > -1) {
                    if (checkPermission) {
                        const cachedTicket = deepLocalCacheCopy[index];
                        deepLocalCacheCopy[index] = item.copyWith({
                            soundPlayed: this.getSoundPlayed(item, cachedTicket),
                            isMeStartingChat: this.getIsMeStartingChat(item, cachedTicket),
                            countUnreadNotification: this.getCountUnreadNotification(item, cachedTicket),
                            messages: item?.messages?.length ? item.messages : [],
                            statusObject: item.statusObject ?? (item.status === cachedTicket.status ? cachedTicket.statusObject : item.statusObject),
                        });
                    } else {
                        deepLocalCacheCopy.splice(index, 1);
                    }
                } else {
                    if (checkPermission) {
                        deepLocalCacheCopy.push(
                            fromSubscription ?
                                item.copyWith({
                                    countUnreadNotification: this.getCountUnreadNotification(item),
                                })
                                :
                                item
                        );
                    }
                }
            }

            this.localCache = deepLocalCacheCopy;
        }
    }

    subscribe(id?: string, ticket?: TicketEntity | null): Observable<TicketEntity[]> {
        return this.subscribeTicketsSocket(id, ticket);
    }

    private subscribeTicketsSocket(
        id?: string,
        ticket?: TicketEntity | null
    ): Observable<TicketEntity[]> {
        return new Observable<TicketEntity[]>((observer: Observer<TicketEntity[]>) => {
            if (!socket) {
                console.error('Socket not connected');
                return;
            }

            if (ticket) {
                this.updateLocalCache([ticket], id, false);
            }

            if (id) {
                this.joinTicket(id);
            }

            const handleChatMessage = async (response: any) => {
                try {
                    const result = SocketResponse.fromJson(new SocketResponseEntity(response));
                    if (result.isInformRooms || result.isEmptyData(this.user)) {
                        return;
                    } else if (result.hasUnknownError) {
                        observer.next(this.localCache);
                    } else {
                        this.normalizeSocketResponse(result);
                        const incomingTickets = result.tickets && result.tickets.length > 0 ? result.tickets : this.localCache;
                        const tickets = await this.getTicketsAfterSubs(incomingTickets, id ?? null);
                        observer.next(tickets);
                    }
                } catch (error) {
                    console.error(error);
                }
            };

            socket.on('message', handleChatMessage);

            return () => {
                socket?.off('message', handleChatMessage);
            };
        });
    };

    private normalizeSocketResponse(result: SocketResponse) {
        try {
            const messages = result.eligibleMessages(this.user);
            const groupedMessages = messages.reduce((acc, message) => {
                if (!acc[message.idTicket]) {
                    acc[message.idTicket] = [];
                }
                acc[message.idTicket].push(message);
                return acc;
            }, {} as Record<string, TicketMessageEntity[]>);

            for (const ticketId of result.getTicketList()) {
                if (!result.tickets?.find(ticket => ticket.id === ticketId)) {
                    const ticket = this.localCache.find((ticket) =>
                        ticket.id === ticketId
                    );
                    if (ticket) {
                        result.tickets = [...(result.tickets ?? []), TicketEntity.fromJson(ticket)];
                    }
                }
                const targetTicket = result.tickets?.find(ticket => ticket.id === ticketId);

                if (targetTicket) {
                    const messages = groupedMessages[targetTicket.id] ?? [];
                    const currentTicketCache = this.localCache.find((ticket) => ticket.id === targetTicket.id);
                    const recentMessage = this.getMostRecentMessage(messages);
                    const isNewLastMessage = !!recentMessage &&
                        (
                            (!currentTicketCache?.lastMessage || !currentTicketCache.lastMessage.createdAt) ||
                            new Date(recentMessage.createdAt as any).getTime() >=
                            new Date(currentTicketCache.lastMessage.createdAt as any).getTime()
                        );
                    const realLastMessage = isNewLastMessage ? recentMessage : currentTicketCache?.lastMessage;
                    const index: number | undefined = result.tickets!.findIndex(item => ticketId == item.id);
                    if (index > -1) {
                        if (currentTicketCache && currentTicketCache.updatedAt && (new Date(currentTicketCache.updatedAt).getTime() > new Date(targetTicket.updatedAt!).getTime())) {
                            result.tickets![index] = TicketEntity.fromJson(currentTicketCache);
                        }
                        const updated = {
                            ...result.tickets![index],
                            lastMessage: this.user.isUserClient && realLastMessage?.isPrivate ? undefined : realLastMessage,
                            messages,
                            notifications: result.notification ? [TicketNotificationEntity.fromJson(result.notification)] : undefined
                        };
                        result.tickets![index] = TicketEntity.fromJson(updated);
                    }

                }
            }
        } catch (error) {
            console.log(`Error ao processar resposta on method: normalizeSocketResponse`);
            console.log(error);
        }
    }

    private getMostRecentMessage(messages: TicketMessageEntity[]): TicketMessageEntity | null {
        if (messages.length === 0) return null;

        return messages.reduce((latest, current) => {
            return new Date(current.createdAt!).getTime() > new Date(latest.createdAt!).getTime()
                ? current
                : latest;
        });
    }

    public sendMessage(message: TicketMessageEntity, associateSenderToTicket: boolean): Promise<TicketMessageEntity> {
        return new Promise<TicketMessageEntity>(async (resolve, reject) => {
            try {
                message = message.copyWith({
                    pushName: this.user.name
                }).prepareToSend();

                const result = await api.post(`chat/writeMessage?associateSenderToTicket=${associateSenderToTicket}`, message);

                message = message.copyWith({
                    id: result.data.id,
                    createdAt: result.data.createdAt,
                    status: result?.data?.status ?? 2,
                    idExternal: result?.data?.idExternal ?? message.idExternal,
                    fromMe: result?.data?.fromMe ?? message.fromMe,
                    participantId: result?.data?.participantId ?? message.participantId,
                });
                return resolve(message);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public subscriptionUploadFile(message: TicketMessageEntity): Observable<TicketMessageEntity> {
        const basePath = `companies/${message.idCompany}/tickets/${message.idTicket}`;
        return message.subscriptionUploadFile(this._remoteDataSource, basePath);
    }


    public editMessage(idMessage: string, text: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                return resolve(await api.patch(`chat/editMessage?id=${idMessage}`, { text }));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public delete(id: string): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                await api.delete(`chat/deleteMessage?id=${id}`);
                return resolve();
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public loadMessagesV2(ticketId: string, page: number, limit: number, cancelTokenSource?: CancelTokenSource, direction?: 'up' | 'down'): Promise<ListPaginationChatMessagesEntity> {
        return new Promise<ListPaginationChatMessagesEntity>(async (resolve, reject) => {
            try {
                const directionParam = direction ? `&direction=${direction}` : '';
                const response = await api.get(
                    `chat/v2/messages?id=${ticketId}&page=${page}&limit=${limit}${directionParam}`,
                    { cancelToken: cancelTokenSource?.token }
                );
                return resolve(ListPaginationChatMessagesEntity.fromJson(response.data));
            } catch (error) {
                const errorParsed = parseServerError(error);
                if (errorParsed.message === DropDeskErrorEnum.operationCanceledDueToNewRequest) {
                    return;
                }
                return reject(errorParsed);
            }
        });
    }

    public loadMessages(chatId: string, page: number, cancelTokenSource?: CancelTokenSource): Promise<ListPaginationEntity<TicketMessageEntity>> {
        return new Promise<ListPaginationEntity<TicketMessageEntity>>(async (resolve, reject) => {
            try {
                const response = await api.get(`chat/messages?id=${chatId}&page=${page}`, { cancelToken: cancelTokenSource?.token });
                const data: TicketMessageEntity[] = response.data.data.map((entry: TicketMessageEntity) => TicketMessageEntity.fromJson(entry));
                const result = new ListPaginationEntity<TicketMessageEntity>({
                    page: response.data.page,
                    limit: response.data.limit,
                    totalRows: response.data.totalRows,
                    hasMore: response.data.page < (response.data.pages - 1),
                    pages: response.data.pages,
                    data,
                });
                return resolve(result);
            } catch (error) {
                const errorParsed = parseServerError(error);
                if (errorParsed.message === DropDeskErrorEnum.operationCanceledDueToNewRequest) {
                    return;
                }
                return reject(errorParsed);
            }
        });
    }

    public navigateToMessage(id: string, ticketNumber: number): Promise<ListPaginationEntity<TicketMessageEntity>> {
        return new Promise<ListPaginationEntity<TicketMessageEntity>>(async (resolve, reject) => {
            try {

                const response = await api.get(`chat/navigate-to-message?id=${id}&ticketNumber=${ticketNumber}`);
                const data: TicketMessageEntity[] = response.data.data.map((entry: TicketMessageEntity) => TicketMessageEntity.fromJson(entry));
                const result = new ListPaginationEntity<TicketMessageEntity>({
                    page: response.data.page,
                    limit: response.data.limit,
                    totalRows: response.data.totalRows,
                    hasMore: response.data.page < (response.data.pages - 1),
                    pages: response.data.pages,
                    data,
                });
                return resolve(result);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public navigateToMessageV2(id: string, limit: number): Promise<ListPaginationChatMessagesEntity> {
        return new Promise<ListPaginationChatMessagesEntity>(async (resolve, reject) => {
            try {
                const response = await api.get(`chat/v2/navigate-to-message?id=${id}&limit=${limit}`);
                return resolve(ListPaginationChatMessagesEntity.fromJson(response.data));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public listChatMessagesAfterSubscription(chatId: string): Promise<TicketMessageEntity[]> {
        return new Promise<TicketMessageEntity[]>(async (resolve, reject) => {
            try {
                const response = await api.get(`chat/messagesAfter?idTicket=${chatId}`);
                const messages = response?.data ?? response?.data?.data;
                const data: TicketMessageEntity[] = messages?.map((entry: Record<string, any>) => TicketMessageEntity.fromJson(entry));
                return resolve(data);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }


    public informReadMessages(ticketId: string, cancelTokenSource?: CancelTokenSource): Promise<void> {
        return new Promise<void>(async (resolve, reject) => {
            try {
                this.resetCountNotification(ticketId);
                return resolve(await api.patch(`chat/informReadMessages?ticketId=${ticketId}`, {}, {cancelToken: cancelTokenSource?.token}));
            } catch (error) {
                const errorParsed = parseServerError(error);
                if (errorParsed.message === DropDeskErrorEnum.operationCanceledDueToNewRequest) {
                    return resolve();
                }
                return reject(errorParsed);
            }
        });
    }

    public updateCacheFromSingleTicket(ticket: TicketEntity) {
        this.updateLocalCache([ticket], ticket.id, false);
    }

    public getTicketFromCache(id: string): TicketEntity | undefined {
        return this.localCache.find(ticket => ticket.id === id);
    }

    public reactMessage(
        messageId: string,
        reactionId: string,
        reaction: string
    ): Promise<VoidSuccess> {
        return new Promise<VoidSuccess>(async (resolve, reject) => {
            try {
                return resolve(await api.post(`chat/reactMessage?id=${messageId}&reactionId=${reactionId}&reaction=${reaction}`));
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    private processReceivedMessagesSocket(tickets: TicketEntity[], fromSingleTicket: string | null, fromSubscription: boolean): void {
        try {
            if (tickets.length) {
                const eligibleTickets = this.filterTicketsForChatCache(tickets, fromSingleTicket);
                if (eligibleTickets.length) {
                    if (this.eligibleProcessingReceived(eligibleTickets)) {
                        this.setProcessingReceived(eligibleTickets);
                        api.patch(`chat/processReceivedMessages?origin=${SubscriptionType.socket}`,
                            {
                                tickets: eligibleTickets.map((entry) => {
                                    return { id: entry.id }
                                })
                            }
                        ).finally(() => {
                            setTimeout(() => {
                                this.removeFromProcessingReceived(eligibleTickets);
                            }, 5000);
                        });
                    }
                    this.addNotificationsDependant(eligibleTickets);
                }
                this.updateLocalCache(tickets, fromSingleTicket, fromSubscription);
            }
        } catch (error) {
            console.log('ERROR processReceivedMessageSocket', error);
        }
    }

    private setProcessingReceived(tickets: TicketEntity[]) {
        const ids = new Set(tickets.map(ticket => ticket.id));
        this.processingReceived = [...this.processingReceived, ...ids];
    }

    private eligibleProcessingReceived(tickets: TicketEntity[]): boolean {
        return !tickets.every(t => this.processingReceived.includes(t.id)) && this.hasMessageNotReceived(tickets, this.user.id);
    }

    private hasMessageNotReceived(tickets: TicketEntity[], userId: string): boolean {
        return tickets?.some(t => t.messages?.some(msg => !msg.received?.[userId])) ?? false;
    }

    private removeFromProcessingReceived(tickets: TicketEntity[]): void {
        if (!tickets?.length || !this.processingReceived?.length) return;

        const idsToRemove = new Set(tickets.map(t => t.id));
        this.processingReceived = this.processingReceived.filter(id => !idsToRemove.has(id));
    }

    private eligibleProcessingRead(ticketId: string): boolean {
        return !this.processingRead.includes(ticketId);
    }

    private setProcessingRead(ticketId: string): void {
        if (!this.processingRead.includes(ticketId)) {
            this.processingRead = [...this.processingRead, ticketId];
        }
    }

    private removeFromProcessingRead(ticketId: string): void {
        if (!ticketId || !this.processingRead?.length) {
            return;
        }
        this.processingRead = this.processingRead.filter(id => id !== ticketId);
    }

    private mergeNotifications(shortTickets: ShortTicketEntity[], tickets: TicketEntity[]): TicketEntity[] {
        const shortTicketsWithNotifications = shortTickets.filter((shortTicket) => shortTicket.notifications);
        shortTicketsWithNotifications.forEach((shortTicket) => {
            const matchingTicket = tickets.find((ticket) => ticket.id === shortTicket.id);
            if (matchingTicket) {
                matchingTicket.notifications = shortTicket.notifications;
            }
        });

        return tickets;
    }

    private addNotificationsDependant(tickets: TicketEntity[]) {
        const oldTickets = this.localCache;
        for (let i = 0; i < tickets.length; i++) {
            const hasNotificationBeforeClose = tickets[i]?.notifications?.find(entry => !!entry.notifiedBeforeClose);
            if (hasNotificationBeforeClose && tickets[i].hasAttendingStatus) {
                const oldTicket = oldTickets.find(oldTicket => oldTicket.id === tickets[i].id);
                const newLastMessage: TicketMessageEntity | undefined = !oldTicket?.lastMessage?.isSystemMessage ? newSystemMessage(tickets[i], hasNotificationBeforeClose, this.user) : oldTicket.lastMessage;
                tickets[i].lastMessage = newLastMessage;
                tickets[i].updatedAt = newLastMessage?.isSystemMessage && hasNotificationBeforeClose.notifiedBeforeClose
                    ?
                    hasNotificationBeforeClose.notifiedBeforeClose
                    :
                    tickets[i].updatedAt;
            }
        }
    }

    private resetCountNotification(ticketId: string): void {
        let index: number | undefined = this.localCache.findIndex(item => ticketId == item.id);
        if (index > -1) {
            this.localCache[index] = this.localCache[index].copyWith({
                countUnreadNotification: 0
            });
        }
    }

    private getSoundPlayed(newTicket: TicketEntity, oldTicket: TicketEntity): boolean {
        return !!newTicket.lastMessage &&
            oldTicket.lastMessage?.id !== newTicket.lastMessage.id &&
            newTicket.hasAttendingStatus &&
            (newTicket.lastMessage.sender?.isUserClient === true || newTicket.lastMessage.isSystemMessage)
    }

    private getIsMeStartingChat(newTicket: TicketEntity, oldTicket: TicketEntity): boolean {
        return (
            (oldTicket.hasOpenStatus && newTicket.hasAttendingStatus) &&
            (Object.keys(newTicket.users).filter((entry) => entry === this.user.id).length > 0)
        )
    }

    private getCountUnreadNotification(newTicket: TicketEntity, ticketCached?: TicketEntity) {
        if (

            (
                (newTicket.lastMessage?.sender?.isUserClient === true && !newTicket.lastMessage?.readers?.[this.user.id])
                ||
                (newTicket.lastMessage?.isSystemMessage)
            )
            &&
            (newTicket.lastMessage?.id !== ticketCached?.lastMessage?.id)
        ) {
            return ((ticketCached?.countUnreadNotification ?? 0) + 1)
        }

        return ticketCached?.countUnreadNotification;
    }

    public resendMessage(id: string): Promise<TicketMessageEntity> {
        return new Promise<TicketMessageEntity>(async (resolve, reject) => {
            try {
                const response = await api.post(`chat/resendMessage?id=${id}`);
                const message = TicketMessageEntity.fromJson(response.data);
                return resolve(message);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }

    public getMessageById(id: string): Promise<TicketMessageEntity> {
        return new Promise<TicketMessageEntity>(async (resolve, reject) => {
            try {
                const response = await api.get(`chat/getMessageById?id=${id}`);
                const message = TicketMessageEntity.fromJson(response.data);
                return resolve(message);
            } catch (error) {
                return reject(parseServerError(error));
            }
        });
    }
}
